// Controls
import { memoizeFunction } from "@fluentui/react";
import { DataFetchingComponent } from "@ms/uno-controls/lib/local/controls/DataFetchingComponent";
import { UnoLeftNav } from "../presentational/UnoLeftNav";
import { UnoLeftNavNewPlanSplitMenuOptions } from "../presentational/UnoLeftNavNewPlanSection";
// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { SubRouteKeys, SubRouteType, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityStatus } from "@ms/uno-constants/lib/local/StoreConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { ComponentNames } from "../../constants/ComponentNames";
import { CdsPackageName } from "@ms/uno-models/lib/local/client/legacyProject/cdsInstance/IPackageInfo";
import { ContainerType } from "@ms/uno-models/lib/local/service/graph-legacy/container/ContainerType";
import { MruApp } from "@ms/uno-models/lib/local/service/mru/MruApp";
// Stores
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
// Styles
import { myDayFilledIconProps, myDayRegularIconProps, myPlansFilledIconProps, myPlansRegularIconProps, myTasksFilledIconProps, myTasksRegularIconProps, pinOffIconProps, portfolioFolderFilledIconProps, portfolioFolderProps } from "@ms/uno-controls/lib/local/icons/icons.styles";
// Utilities
import { NavView } from "@ms/uno-controls/lib/local/controls/presentational/unoSwitchViewDropdown/UnoSwitchViewDropdown";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { isMruItemAPlan } from "@ms/uno-models/lib/local/utilities/MruAppUtilities";
import { Strings as SharedStrings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { extractEntityRoutingIdFromSubRoute } from "@ms/uno-routing";
import { isStatusFailure, isStatusFetchingOrUnfetched } from "@ms/uno-stores/lib/local/utilities/StatusUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { compareByDisplayName, compareTemporalDate } from "@ms/uno-utilities/lib/local/Comparators";
import React from "react";
import { loadTranslations, Strings } from "../../utilities/CultureUtilities";
/**
 * Enumeration of the static options for Uno LeftNav
 */ var LeftNavStaticOptions = /*#__PURE__*/ function(LeftNavStaticOptions) {
    /** The my day view */ LeftNavStaticOptions["myDay"] = "myDay";
    /** The plan hub view */ LeftNavStaticOptions["myPlans"] = "myPlans";
    /** The my tasks view */ LeftNavStaticOptions["myTasks"] = "myTasks";
    /** The my portfolio view */ LeftNavStaticOptions["myPortfolios"] = "myPortfolio";
    return LeftNavStaticOptions;
}(LeftNavStaticOptions || {});
/**
 * Uno Left Nav container. This is the top level smart component for the left nav
 */ export class UnoLeftNavContainer extends DataFetchingComponent {
    /**
     * Name of the component
     */ name() {
        return ComponentNames.UnoLeftNavContainer;
    }
    getStoresToSubscribeTo() {
        // Return the array of stores that this component needs to subscribe to
        return [
            Store.AppContext,
            Store.Capabilities,
            Store.LegacyContainer,
            Store.Groups,
            Store.Mru,
            Store.Org,
            Store.PremiumPlanPermissions,
            Store.Portfolio,
            Store.Route,
            Store.Teams,
            Store.Tenant,
            Store.LegacyView
        ];
    }
    /**
     * Fetch any critical data necessary for this component and its children
     * @param props Props for the component
     * @param state State of the component
     */ async fetchCriticalData(props, state) {
        const { actionCreatorProviders, configProvider } = props;
        if (!configProvider().flights.EnableDataFetchViaWorker) {
            const viewAc = await actionCreatorProviders.leftNavView();
            viewAc.fetchViewData();
        }
    }
    /**
     * Fetch any non-critical data necessary for this component and its children
     * @param props Props for the component
     * @param state State of the component
     */ fetchNonCriticalData(props, state) {
    // Empty
    }
    /**
     * Method to check whether all critical data required for user interaction is available
     */ checkForCriticalDataReady() {
        const mruStore = this.props.storeLoader.load(Store.Mru);
        const pinnedItems = mruStore?.getPinnedItems();
        const isMruFetched = pinnedItems != null && pinnedItems.status !== StoreEntityStatus.Unfetched;
        if (isMruFetched) {
            this.onPageCriticalDataFetchCompletion();
        }
        return isMruFetched;
    }
    getRenderState() {
        const { storeLoader, configProvider } = this.props;
        const [mruStore, orgStore, tenantStore, portfolioStore, premiumPlanPermissionsStore] = storeLoader.loadAll([
            Store.Mru,
            Store.Org,
            Store.Tenant,
            Store.Portfolio,
            Store.PremiumPlanPermissions
        ]);
        // Get critical data from stores
        const pinnedItems = mruStore?.getPinnedItems();
        const isMruFetched = pinnedItems != null && pinnedItems.status !== StoreEntityStatus.Unfetched;
        const isMruDataLoaded = pinnedItems != null && pinnedItems.data.length >= configProvider().settings.performanceSettings.minimumPlansForVisualReadiness;
        // Get non-critical data from stores
        const isOrgDataFetched = orgStore != null && !isStatusFetchingOrUnfetched(orgStore.getDefaultCdsInstance().status);
        const isTenantDataFetchedOrNotNeeded = !configProvider().flights.EnablePortfolioExperience || tenantStore != null && !isStatusFetchingOrUnfetched(tenantStore.getCurrentTenantPortfoliosEnabledSetting().status);
        const shouldFetchPortfolioItems = configProvider().changeGates.DisableLicenseCheckForGetPortfoliosCall || premiumPlanPermissionsStore?.userHasP3PlusLicense();
        const isPortfolioDataFetchedOrNotNeeded = !configProvider().flights.EnablePortfolioExperience || (shouldFetchPortfolioItems ? portfolioStore != null && !isStatusFetchingOrUnfetched(portfolioStore.getPortfolioItems().status) : true);
        let state = ViewRenderState.None;
        if (isOrgDataFetched && isTenantDataFetchedOrNotNeeded && isPortfolioDataFetchedOrNotNeeded && isMruFetched) {
            state = ViewRenderState.All;
        } else if (isMruDataLoaded || isMruFetched) {
            state = ViewRenderState.Critical;
        }
        if (state === ViewRenderState.Critical || state === ViewRenderState.All) {
            this.onPageCriticalDataFetchCompletion();
        }
        return state;
    }
    /**
     * Render the Leftnav
     */ renderComponent() {
        const [routeStore, appContextStore, viewStore] = this.props.storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.LegacyView
        ]);
        const isFullFrameMode = appContextStore.isFullFrameMode();
        return /*#__PURE__*/ React.createElement(UnoLeftNav, {
            staticOptions: this.getLeftNavStaticOptions(routeStore.getCurrentSubRouteType(), this.checkForMyPortfolioOptionVisibility()),
            pinnedOptions: this.getLeftNavPinnedOptions(),
            newPlanInfo: this.getLeftNavNewPlanInfo(),
            switchViewDropdownOptions: this.getSwitchViewDropdownOptions(),
            shouldRenderPinnedError: this.getShouldRenderPinnedError(),
            onRetryButtonClicked: this.fetchMruItems,
            showAppInfo: !isFullFrameMode,
            calloutDetails: this.getCalloutDetails(),
            isLeftNavCollapsed: !(viewStore?.getLeftNavExpanded() ?? true),
            setIsLeftNavCollapsed: this.setIsLeftNavCollapsed
        });
    }
    /**
     * Get the pinned options for Uno LeftNav (Temporary)
     */ getLeftNavPinnedOptions() {
        const { storeLoader } = this.props;
        const [mruStore, routeStore, teamsStore] = storeLoader.loadAll([
            Store.Mru,
            Store.Route,
            Store.Teams
        ]);
        const currentRouteType = routeStore.getCurrentSubRouteType();
        const contextMenuItemsWithUnpinOption = (mruServerDocId)=>{
            const unpinContextmenuItem = {
                key: Strings.UnoLeftNav_UnpinText,
                text: Strings.UnoLeftNav_UnpinText,
                iconProps: pinOffIconProps,
                onClick: ()=>{
                    this.onUnpinClick(mruServerDocId);
                }
            };
            return [
                unpinContextmenuItem
            ];
        };
        const currentSubRoute = routeStore?.getCurrentSubRoute();
        const pinnedItems = mruStore?.getPinnedItems().data ?? [];
        const currentSelectedPlanId = extractEntityRoutingIdFromSubRoute(currentSubRoute, currentRouteType);
        // Filter out pinned items that are not plans. Sort the pinned items in descending order of pinned time stamp
        const filteredAndSortedPinnedItems = pinnedItems.filter((pinnedItem)=>isMruItemAPlan(pinnedItem)).sort(this.comparePinnedItem);
        const pinnedOptions = filteredAndSortedPinnedItems.map((mruItem)=>{
            // Find the associated item in the retail services tabs, if any
            const tab = teamsStore?.getTeamsTasksTabsForPlan(mruItem.serverDocId)?.[0] ?? null;
            const tabChannelId = tab?.channelId ?? null;
            const containerType = mruItem.getContainerType();
            return {
                id: mruItem.serverDocId,
                name: mruItem.title,
                selected: currentSelectedPlanId === mruItem.serverDocId,
                breadcrumbItems: this.getBreadcrumbItems(mruItem, tabChannelId),
                contextMenuItems: contextMenuItemsWithUnpinOption(mruItem.serverDocId),
                containerType: containerType ?? undefined,
                containerPhoto: this.getContainerPhoto(mruItem.getContainerId() ?? "", containerType) ?? undefined,
                onSelect: ()=>{
                    this.onSelectCollection(mruItem);
                },
                onRenderVirtualizedItem: ()=>{
                    this.onRenderVirtualizedItem(mruItem, tabChannelId);
                }
            };
        });
        return pinnedOptions;
    }
    /**
     * Generate new plan option for Uno LeftNav
     */ getLeftNavNewPlanInfo() {
        const { configProvider } = this.props;
        const { isNewPortfolioCalloutVisible } = this.state;
        const newPlanProps = {
            onSelect: (optionType)=>{
                this.onSelectNewOption(optionType);
            },
            // disable portfolio experience for FR/DE environment
            isSplitButtonEnabled: configProvider().flights.EnablePortfolioExperience && configProvider().settings.EnvironmentFeatureSettings.EnablePortfolioExperience,
            // Toggling the callout visibility using isNewPortfolioCalloutVisible.
            // Change in state triggers a re-render of component toggling callout visibility.
            onDismissCallout: ()=>{
                this.setState({
                    isNewPortfolioCalloutVisible: false
                });
            },
            isNewPortfolioCalloutVisible: isNewPortfolioCalloutVisible
        };
        return newPlanProps;
    }
    /**
     * Method to get the breadcrumb information for Pinned items
     * @param mruItem The mru item
     * @param channelId  The channel id
     */ getBreadcrumbItems(mruItem, channelId) {
        const breadcrumbs = [];
        if (mruItem.app === MruApp.ToDo) return breadcrumbs;
        const userId = this.props.storeLoader.load(Store.AppContext).getAppContext().userId ?? "";
        const containerId = mruItem.getContainerId() ?? userId;
        const containerType = mruItem.getContainerType();
        const channelName = channelId ? this.getChannelName(channelId) : null;
        const containerName = containerId ? this.getContainerName(containerId) : null;
        if (containerType === ContainerType.Group) {
            // If the container is a group push the group name
            containerName != null && breadcrumbs.push(containerName);
            // If there is an associated channel, push the channel name
            channelName != null && breadcrumbs.push(channelName);
        } else if (containerType === ContainerType.Roster) {
        // TODO: Get plan creation context from fetched plan
        }
        return breadcrumbs;
    }
    getPublishingCapability() {
        const capabilitiesStore = this.props.storeLoader.load(Store.Capabilities);
        if (!capabilitiesStore) {
            return false;
        }
        return capabilitiesStore.getIsPublishingEnabled().data;
    }
    /**
     * Initializes a new instance of the UnoLeftNavContainer
     * @param props Props for the MyPlansPage component
     * @param [context] Context to pass onto the base component
     */ constructor(props, context){
        super(props, context), /**
     * Called when page detects critical data is ready during "checkForCriticalDataReady" and calls view action creator onCriticalDataFetchCompletion
     * For performance and to avoid component update loops this method executes once per viewPartitionKey on the view component.
     */ this.onPageCriticalDataFetchCompletion = async ()=>{
            const viewAc = await this.props.actionCreatorProviders.leftNavView();
            // CheckForCriticalDataReady is called several times during component lifecycle, tracks dispatched event to make sure it runs once per view partition key
            if (!this.onCriticalDataReadyDispatched) {
                this.onCriticalDataReadyDispatched = true;
                viewAc.onCriticalDataFetchCompletion();
            }
        }, /**
     * Method to set the left nav collapsed state
     * @param isCollapsed The collapsed state to set
     */ this.setIsLeftNavCollapsed = async (isCollapsed)=>{
            const viewAc = await this.props.actionCreatorProviders.viewTransforms();
            viewAc.setLeftNavExpanded(!isCollapsed);
        }, /**
     * Method to check whether My Portfolios option should be visible for a user or not.
     */ this.checkForMyPortfolioOptionVisibility = ()=>{
            const { configProvider, storeLoader } = this.props;
            // If the flight is not enabled, the user will not see the My Portfolios option irrespective of the license.
            const isPortfoliosExperienceEnabled = configProvider().flights.EnablePortfolioExperience;
            // If the environment is FR/DE, the user will not see the My Portfolios option.
            if (!isPortfoliosExperienceEnabled || !configProvider().settings.EnvironmentFeatureSettings.EnablePortfolioExperience || configProvider().settings.disableDataverse) {
                return false;
            }
            const [premiumPlanPermissionsStore, portfolioStore] = storeLoader.loadAll([
                Store.PremiumPlanPermissions,
                Store.Portfolio
            ]);
            // Check if the user has the necessary license for accessing My Portfolios Option
            const userHasP3PlusLicense = premiumPlanPermissionsStore?.userHasP3PlusLicense();
            if (userHasP3PlusLicense) {
                return true;
            }
            // If user does not P3+ license, but user was shared at least one portfolio, then user should see the My Portfolios option.
            const userPortfolioItems = portfolioStore?.getPortfolioItems()?.data;
            const userHasPortfolioItems = userPortfolioItems && userPortfolioItems.length > 0;
            return !!userHasPortfolioItems;
        }, /**
     * Get the channel name. Null if not found
     * @param channelId The channel id
     */ this.getChannelName = (channelId)=>{
            const teamsStore = this.props.storeLoader.load(Store.Teams);
            return teamsStore?.getChannel(channelId)?.displayName ?? null;
        }, /**
     * Callback to get the container name for a plan
     * @param id The container id
     */ this.getContainerName = (id)=>{
            const containerStore = this.props.storeLoader.load(Store.LegacyContainer);
            return containerStore?.getContainer(id).data?.displayName ?? null;
        }, /**
     * Method to get the container photo
     * @param id The container id
     * @param containerType The container type
     */ this.getContainerPhoto = (id, containerType)=>{
            const groupsStore = this.props.storeLoader.load(Store.Groups);
            if (containerType === ContainerType.Group && id !== null) {
                return groupsStore?.getGroupPhotoById(id) ?? null;
            }
            return null;
        }, /**
     * Generate the static options for Uno LeftNav
     */ this.getLeftNavStaticOptions = memoizeFunction((currentRouteType, isMyPortfoliosOptionVisible)=>{
            const myDayIcons = {
                normalIcon: myDayRegularIconProps,
                focusedIcon: myDayFilledIconProps
            };
            const myDayStaticOption = {
                id: ElementIds.MyDayNavButton,
                name: SharedStrings.Views_MyDayTitle,
                selected: currentRouteType === SubRouteType.MyDay,
                icon: myDayIcons,
                onSelect: ()=>{
                    this.onSelectStaticOption("myDay");
                }
            };
            const myTasksIcons = {
                normalIcon: myTasksRegularIconProps,
                focusedIcon: myTasksFilledIconProps
            };
            const myTasksStaticOption = {
                id: ElementIds.MyTasksNavButton,
                name: SharedStrings.Views_MyTasksTitle,
                selected: currentRouteType === SubRouteType.MyTasks,
                icon: myTasksIcons,
                onSelect: ()=>{
                    this.onSelectStaticOption("myTasks");
                }
            };
            const myPlanIcons = {
                normalIcon: myPlansRegularIconProps,
                focusedIcon: myPlansFilledIconProps
            };
            const myPlanStaticOption = {
                id: ElementIds.MyPlansNavButton,
                name: SharedStrings.Views_MyPlansTitle,
                selected: currentRouteType === SubRouteType.MyPlans,
                icon: myPlanIcons,
                onSelect: ()=>{
                    this.onSelectStaticOption("myPlans");
                }
            };
            const myPortfolioIcons = {
                normalIcon: portfolioFolderProps,
                focusedIcon: portfolioFolderFilledIconProps
            };
            const myPortfolioStaticOption = {
                id: ElementIds.MyPortfoliosNavButton,
                name: SharedStrings.Views_MyPortfoliosTitle,
                selected: currentRouteType === SubRouteType.MyPortfolios,
                icon: myPortfolioIcons,
                onSelect: ()=>{
                    this.onSelectStaticOption("myPortfolio");
                }
            };
            const leftNavStaticOptions = {
                myDayInfo: myDayStaticOption,
                myTasksInfo: myTasksStaticOption,
                myPlansInfo: myPlanStaticOption,
                myPortfoliosInfo: isMyPortfoliosOptionVisible ? myPortfolioStaticOption : null
            };
            return leftNavStaticOptions;
        }), /**
     * Callback for group photo fetch when leftnav item is rendered
     * @param mruItem The mru item
     */ this.onRenderVirtualizedItem = async (mruItem, channelId)=>{
            const { actionCreatorProviders, storeLoader } = this.props;
            const [appContextStore, containerStore, groupsStore, teamsStore] = storeLoader.loadAll([
                Store.AppContext,
                Store.LegacyContainer,
                Store.Groups,
                Store.Teams
            ]);
            const userId = appContextStore.getAppContext().userId ?? "";
            const containerId = mruItem.getContainerId() ?? userId;
            const containerType = mruItem.getContainerType();
            if (containerType === ContainerType.Group) {
                const [containerAc, groupsAc, teamsAc] = await Promise.all([
                    actionCreatorProviders.container(),
                    actionCreatorProviders.groups(),
                    actionCreatorProviders.teams()
                ]);
                // Fetch container if it is not already fetched
                if (containerStore != null && containerStore.getContainer(containerId).status === StoreEntityStatus.Unfetched) {
                    containerAc.fetchContainer({
                        externalId: containerId,
                        containerType: containerType
                    });
                }
                // Fetch group photos if they are not already fetched
                if (groupsStore != null && groupsStore.getGroupPhotoById(containerId) == null) {
                    groupsAc.fetchPhotosForGroupSet([
                        containerId
                    ]);
                }
                // Fetch channel if its associated with a tab
                if (channelId != null && teamsStore != null && teamsStore.getChannel(channelId) == null) {
                    teamsAc.fetchChannel(containerId, channelId);
                }
            }
        }, /**
     * Callback when a leftNav static option is selected
     * @param option Id of the static option
     */ this.onSelectStaticOption = async (option)=>{
            const { loggers, actionCreatorProviders } = this.props;
            const routeActionCreator = actionCreatorProviders.routing();
            loggers.traceLogger.logTrace(0x1e48881d /* tag_4si63 */ , TraceLevel.Info, `LeftNav static option selected [Option=${option}]`);
            switch(option){
                case "myDay":
                    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                        option: `${"myDay"}`
                    });
                    routeActionCreator.navigate({
                        subRouteType: SubRouteType.MyDay,
                        subRoute: this.getDefaultMyDaySubRoute()
                    });
                    break;
                case "myPlans":
                    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                        option: `${"myPlans"}`
                    });
                    routeActionCreator.navigate({
                        subRouteType: SubRouteType.MyPlans,
                        subRoute: []
                    });
                    break;
                case "myPortfolio":
                    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                        option: `${"myPortfolio"}`
                    });
                    routeActionCreator.navigate({
                        subRouteType: SubRouteType.MyPortfolios,
                        subRoute: []
                    });
                    break;
                case "myTasks":
                    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                        option: `${"myTasks"}`
                    });
                    routeActionCreator.navigate({
                        subRouteType: SubRouteType.MyTasks,
                        subRoute: []
                    });
                    break;
                default:
                    ErrorUtilities.unreachableCase(option, (log)=>{
                        loggers.traceLogger.logTrace(0x1e48881c /* tag_4si62 */ , TraceLevel.Error, log);
                    });
            }
        }, /**
     * Callback when a create new option is selected
     * @param optionType Type of option to create - plan or portfolio
     * @param target The target element on which the callout is shown
     */ this.onSelectNewOption = async (optionType)=>{
            const { loggers, actionCreatorProviders, storeLoader } = this.props;
            const premiumPlanPermissionsStore = storeLoader.load(Store.PremiumPlanPermissions);
            const modalActionCreator = await actionCreatorProviders.modal();
            if (optionType === UnoLeftNavNewPlanSplitMenuOptions.Plan) {
                loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.NewPlanButtonClicked);
                modalActionCreator.openCreatePlanModal({
                    showCreateNewPremium: false,
                    isTeamsTab: false
                });
            } else if (optionType === UnoLeftNavNewPlanSplitMenuOptions.Portfolio) {
                loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.NewPortfolioButtonClicked);
                // P3+ licensed users can create portfolios if portfolios are enabled and only if the lower version of the package is not installed
                if (premiumPlanPermissionsStore?.userHasP3PlusLicense() && this.hasTenantEnabledPortfolios() && !this.isLowerPortfolioPackageVersionInstalled()) {
                    modalActionCreator.openCreatePortfolioModal();
                } else {
                    this.setState({
                        isNewPortfolioCalloutVisible: true
                    });
                }
            }
        }, /**
     * Callback when a leftNav pinned collection is selected
     * @param mruItem Mru item that was selected
     */ this.onSelectCollection = (mruItem)=>{
            const { loggers, actionCreatorProviders, storeLoader } = this.props;
            const orgStore = storeLoader.load(Store.Org);
            const routeActionCreator = actionCreatorProviders.routing();
            loggers.traceLogger.logTrace(0x1e48881b /* tag_4si61 */ , TraceLevel.Info, `LeftNav pinned collection selected [CollectionId=${mruItem.serverDocId}][MruId=${mruItem.mruId}][Type=${mruItem.app}]`);
            loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.OptionClicked, {
                option: `${mruItem.app}`
            });
            // TODO: Fix the routed route for TODO
            switch(mruItem.app){
                case MruApp.Planner:
                    routeActionCreator.navigateToPlan({
                        planId: mruItem.serverDocId,
                        planType: PlanType.Basic,
                        planName: mruItem.title
                    });
                    break;
                case MruApp.Project:
                    const typedProjectMetadata = mruItem.metadata;
                    if (mruItem.isProjectOnlinePlan() || mruItem.isProjectOperationsPlan()) {
                        loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanClicked, {
                            metadataType: `${typedProjectMetadata.metadataType}`
                        });
                        safelyOpenExternalUrl(mruItem.url, loggers.traceLogger);
                    } else if (mruItem.isNamedOrgPlan()) {
                        const orgId = typedProjectMetadata.orgId;
                        routeActionCreator.navigateToPlan({
                            orgId: orgId ?? "",
                            planId: mruItem.serverDocId,
                            planType: PlanType.Premium,
                            planName: mruItem.title
                        });
                        loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanClicked, {
                            metadataType: `${typedProjectMetadata.metadataType}`,
                            orgType: `${typedProjectMetadata.orgType}`
                        });
                    } else {
                        let orgId = typedProjectMetadata.orgId ?? orgStore?.getDefaultCdsInstance().data?.orgId;
                        if (orgId == null) {
                            loggers.traceLogger.logTrace(0x1e35030c /* tag_4nqmm */ , TraceLevel.Warning, `LeftNav project selected but orgId was not found [ProjectId=${mruItem.serverDocId}]`);
                            // Fallback for now to current behavior
                            orgId = typedProjectMetadata.orgType;
                        }
                        routeActionCreator.navigateToPlan({
                            orgId: orgId,
                            planId: mruItem.serverDocId,
                            planType: PlanType.Premium,
                            planName: mruItem.title
                        });
                    }
                    break;
                case MruApp.ToDo:
                    routeActionCreator.navigateToPlan({
                        planId: mruItem.serverDocId,
                        planType: PlanType.ToDo,
                        planName: mruItem.title
                    });
                    break;
                default:
                    loggers.traceLogger.logTrace(0x1e48881a /* tag_4si60 */ , TraceLevel.Warning, `LeftNav unsupported collection type selected [CollectionId=${mruItem.serverDocId}][MruId=${mruItem.mruId}][Type=${mruItem.app}]`);
                    return;
            }
        }, /**
     * Callback when the unpin context menu item is clicked
     * @param serverDocId ServerDocId of the mru item
     */ this.onUnpinClick = async (serverDocId)=>{
            const { loggers, actionCreatorProviders, storeLoader } = this.props;
            const mruStore = storeLoader.load(Store.Mru);
            const mruActionCreator = await actionCreatorProviders.mru();
            loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.PlanPinned, {
                isPinned: "false"
            });
            const mruItem = mruStore?.getMruItemByServerDocId(serverDocId) ?? null;
            if (mruItem == null) {
                loggers.traceLogger.logTrace(0x1e488819 /* tag_4si6z */ , TraceLevel.Warning, `LeftNav unpin clicked but mru item was not present in the store [ServerDocId=${serverDocId}]`);
                return;
            }
            const updatedMruItem = mruItem.setProperty("isPinned", false);
            mruActionCreator.upsertMruItem(updatedMruItem);
        }, this.onSwitchView = ()=>{
            const { loggers, actionCreatorProviders } = this.props;
            const routeActionCreator = actionCreatorProviders.routing();
            loggers.traceLogger.logTrace(0x1e48881e /* tag_4si64 */ , TraceLevel.Info, `LeftNav view switched [View=${SubRouteType.Publishing}]`);
            loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.LeftNav, EngagementTelemetryConstants.SwitchView, {
                view: `${SubRouteType.Publishing}`
            });
            routeActionCreator.navigate({
                subRouteType: SubRouteType.Publishing,
                subRoute: []
            });
        }, this.getSwitchViewDropdownOptions = ()=>{
            return {
                onChange: this.onSwitchView,
                isPublisherViewVisible: this.getPublishingCapability(),
                currentView: NavView.tasksView,
                strings: {
                    tasksView: Strings.UnoLeftNav_DropdownTasksViewOption,
                    publisherView: Strings.UnoLeftNav_PublishTasksViewOption
                }
            };
        }, /**
     * Custom compare function for sorting Pinned Items in the left nav
     * @param a The first item to compare
     * @param b The second item to compare
     */ this.comparePinnedItem = (a, b)=>{
            const timeStampA = a.getLastPinnedTimeStamp();
            const timeStampB = b.getLastPinnedTimeStamp();
            // Sort first by lastPinnedTimeStamp. Items without a lastPinnedTimeStamp should go after the recent items.
            // Secondarily, sort by display name.
            if (timeStampA == null && timeStampB == null) {
                return compareByDisplayName(a, b, (item)=>item.title);
            }
            if (timeStampA == null) {
                return 1;
            }
            if (timeStampB == null) {
                return -1;
            }
            return compareTemporalDate(timeStampB, timeStampA);
        }, /**
     * Provide the default subRoute for My Day if the subRoute is empty
     */ this.getDefaultMyDaySubRoute = ()=>{
            return [
                SubRouteKeys.View,
                ViewType.Grid
            ];
        }, /**
     * Method to check if the error message should be rendered for the pinned section
     */ this.getShouldRenderPinnedError = ()=>{
            const mruStore = this.props.storeLoader.load(Store.Mru);
            if (mruStore == null) {
                return false;
            }
            const pinnedItems = mruStore.getPinnedItems();
            return pinnedItems.data.length === 0 && isStatusFailure(pinnedItems.status);
        }, /**
     * Fetches the pinned and recent mru items
     */ this.fetchMruItems = async ()=>{
            const mruActionCreator = await this.props.actionCreatorProviders.mru();
            mruActionCreator.fetchMruItems();
        }, /**
     * Method to check if portfolios are enabled
     */ this.hasTenantEnabledPortfolios = ()=>{
            const tenantStore = this.props.storeLoader.load(Store.Tenant);
            return tenantStore?.getCurrentTenantPortfoliosEnabledSetting().data ?? false;
        }, /**
     * Helper method to check if minimum version of portfolios solution is installed
     * @remarks
     * TODO #9795335 :Will remove this once the Portfolio Provisioning flight is graduated
     */ this.isPortfoliosSolutionInstalled = ()=>{
            const orgStore = this.props.storeLoader.load(Store.Org);
            return (orgStore?.isPackageReady(CdsPackageName.Roadmap) && orgStore.isMinimumPackageVersionInstalled(CdsPackageName.Roadmap)) ?? false;
        }, /**
     * Helper method to check if the portfolio solution installed is lower than minimum required version
     * @returns boolean indicating whether lower Portfolio package version is installed
     */ this.isLowerPortfolioPackageVersionInstalled = ()=>{
            const orgStore = this.props.storeLoader.load(Store.Org);
            if (this.props.configProvider().flights.EnableUnoLevelPortfolioProvisioning) {
                const isPackageReady = orgStore?.isPackageReady(CdsPackageName.Roadmap) ?? false;
                const isMinVersionInstalled = orgStore?.isMinimumPackageVersionInstalled(CdsPackageName.Roadmap) ?? false;
                return isPackageReady && !isMinVersionInstalled;
            }
            return !this.isPortfoliosSolutionInstalled();
        }, /**
     * Get the callout details for the portfolio option
     */ this.getCalloutDetails = ()=>{
            const isPortfoliosEnabled = this.hasTenantEnabledPortfolios();
            if (!isPortfoliosEnabled) {
                return {
                    calloutTitle: Strings.UnoLeftNav_NewPlanPortfolioNotEnabledCalloutTitle,
                    calloutDescription: Strings.UnoLeftNav_NewPlanPortfolioNotEnabledCalloutDescription,
                    calloutLinkText: ""
                };
            } else if (this.isLowerPortfolioPackageVersionInstalled()) {
                return {
                    calloutTitle: Strings.UnoLeftNav_PortfoliosSolutionNotInstalledCalloutTitle,
                    calloutDescription: Strings.UnoLeftNav_PortfoliosSolutionNotInstalledCalloutDescription,
                    calloutLinkText: ""
                };
            }
            return {
                calloutTitle: Strings.UnoLeftNav_NewPlanCalloutTitle,
                calloutDescription: Strings.UnoLeftNav_NewPlanCalloutDescription,
                calloutLinkText: Strings.UnoLeftNav_NewPlanCalloutLinkText
            };
        };
        this.state = {
            isNewPortfolioCalloutVisible: false
        };
        // Load translations for the current locale
        loadTranslations(this.props.storeLoader.load(Store.AppContext).getAppContext().locale);
    }
}
